import React, { useCallback, useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'
import { ADMIN_GET_TENANT_SPIDS } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

function stateSummary(spids) {
    const counts = {}
    for (const s of spids) {
        const state = s.state || 'Unknown'
        counts[state] = (counts[state] || 0) + 1
    }
    return Object.entries(counts)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([state, count]) => `${count} ${state}`)
        .join(', ')
}

function formatTime(t) {
    if (!t) return '—'
    try { return new Date(t).toLocaleString() } catch { return t }
}

function surveyList(spid) {
    const surveys = spid.surveys
    if (!surveys || typeof surveys !== 'object') return '—'
    return Object.keys(surveys).join(', ') || '—'
}

export function SpidStatusTable({ tenantId, refreshToken }) {
    const [spids, setSpids] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const load = useCallback(() => {
        setLoading(true)
        setError(null)
        ADMIN_GET_TENANT_SPIDS(tenantId)
            .then(result => {
                const items = Array.isArray(result) ? result : []
                items.sort((a, b) => (b.created_time || '').localeCompare(a.created_time || ''))
                setSpids(items)
            })
            .catch(err => setError(getApiErrorMessage(err, 'Could not load SPIDs')))
            .finally(() => setLoading(false))
    }, [tenantId])

    useEffect(() => { load() }, [load, refreshToken])

    return (
        <div className="sa-mock-spid-table">
            <div className="sa-mock-section-head">
                <Typography variant="subtitle2">SPID Status</Typography>
                <Button variant="outlined" size="small" onClick={load} disabled={loading}>
                    {loading ? <CircularProgress size={14} /> : 'Refresh'}
                </Button>
            </div>
            {error && (
                <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>{error}</Typography>
            )}
            {!loading && spids.length > 0 && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                    {spids.length} SPIDs: {stateSummary(spids)}
                </Typography>
            )}
            <Table size="small" className="superadmin-data-table">
                <TableHead>
                    <TableRow>
                        <TableCell>SP ID</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Surveys</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Last Response</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={5}><CircularProgress size={18} /></TableCell>
                        </TableRow>
                    )}
                    {!loading && spids.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Typography variant="body2" color="textSecondary">
                                    No SPIDs for this tenant yet. Add mock employees and run the integration.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && spids.map(s => (
                        <TableRow key={s.sp_id}>
                            <TableCell className="is-mono" style={{ fontSize: 11 }}>{s.sp_id}</TableCell>
                            <TableCell>{s.state}</TableCell>
                            <TableCell>{surveyList(s)}</TableCell>
                            <TableCell style={{ fontSize: 11 }}>{formatTime(s.created_time)}</TableCell>
                            <TableCell style={{ fontSize: 11 }}>{formatTime(s.last_response_time)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}
